// styles.ts — inline style objects. Kept as TS so the artifact stays self-contained
// without a CSS pipeline. Easy to swap to CSS Modules / Tailwind / styled-components later.

import type { CSSProperties } from "react";

// ── Theme tokens ─────────────────────────────────────────────────────────
// In-game styles read theme tokens via `var(--*)` with Ember Hollow
// fallbacks. The fallbacks are the canonical values used pre-multi-scenario
// — they paint the default amber/dark-brown gothic palette when no
// ScenarioThemeWrapper is mounted (or when the scenarioId is unknown).
//
// The wrapper sits in `web/src/scenarios/ScenarioThemeWrapper.tsx` and is
// applied at the top of the in-game render tree by App.tsx. As the user
// switches stories, the wrapper's inline custom-property values change
// and every styled surface re-paints together.
//
// Login-screen styles (loginShell, loginCard, landingPane, ...) use the
// same vars. Modal styles (modalBackdrop, modalCard, ...) deliberately do
// NOT — modals stay in their fixed gothic palette over whatever game
// theme is below.
const FALLBACK_BG_GRADIENT =
  "radial-gradient(ellipse at top, #1a1410 0%, #0d0907 60%, #050302 100%)";
const FALLBACK_ACCENT = "#d4a574";
const FALLBACK_ACCENT_MUTED = "rgba(212,165,116,0.45)";
const FALLBACK_INK = "#e8d9b8";
const FALLBACK_INK_MUTED = "#8a7355";
const FALLBACK_PAPER = "rgba(20,14,10,0.55)";
const FALLBACK_PAPER_EDGE = "rgba(212,165,116,0.18)";
const FALLBACK_FONT_BODY =
  "'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif";

// Compose helpers so the var() expressions stay readable inline.
const VAR_BG = `var(--bg-gradient, ${FALLBACK_BG_GRADIENT})`;
const VAR_ACCENT = `var(--accent, ${FALLBACK_ACCENT})`;
const VAR_ACCENT_MUTED = `var(--accent-muted, ${FALLBACK_ACCENT_MUTED})`;
const VAR_INK = `var(--ink, ${FALLBACK_INK})`;
const VAR_INK_MUTED = `var(--ink-muted, ${FALLBACK_INK_MUTED})`;
const VAR_PAPER = `var(--paper, ${FALLBACK_PAPER})`;
const VAR_PAPER_EDGE = `var(--paper-edge, ${FALLBACK_PAPER_EDGE})`;
const VAR_FONT_BODY = `var(--font-body, ${FALLBACK_FONT_BODY})`;

// In-game theme transition — applied to surfaces that should fade between
// themes rather than snap when the user switches stories.
const THEME_TRANSITION =
  "background 0.4s ease-out, color 0.4s ease-out, border-color 0.4s ease-out";

export const styles: Record<string, CSSProperties> = {
  shell: {
    position: "relative",
    height: "100vh",
    background: VAR_BG,
    color: VAR_INK,
    fontFamily: VAR_FONT_BODY,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    transition: THEME_TRANSITION,
  },
  grain: {
    position: "fixed",
    inset: 0,
    pointerEvents: "none",
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.6 0 0 0 0 0.5 0 0 0 0 0.3 0 0 0 0.08 0'/></filter><rect width='200' height='200' filter='url(%23n)'/></svg>\")",
    opacity: 0.5,
    zIndex: 1,
    mixBlendMode: "overlay",
  },
  header: {
    position: "relative",
    // Above main (zIndex: 2) so absolute-positioned dropdowns from header
    // pills can hang below the header without being occluded by main.
    zIndex: 10,
    flexShrink: 0,
    padding: "28px 40px 20px",
    borderBottom: `1px solid ${VAR_PAPER_EDGE}`,
    background:
      "linear-gradient(to bottom, rgba(20,14,10,0.6), transparent)",
    transition: THEME_TRANSITION,
  },
  titleRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 16,
    flexWrap: "wrap",
  },
  title: {
    margin: 0,
    fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
    fontSize: "2.4rem",
    fontWeight: 400,
    color: VAR_INK,
    letterSpacing: "0.02em",
    textShadow: "0 0 24px rgba(0,0,0,0.4)",
    transition: "color 0.4s ease-out",
  },
  subtitle: {
    fontStyle: "italic",
    color: VAR_INK_MUTED,
    fontSize: "0.85rem",
    letterSpacing: "0.08em",
    transition: "color 0.4s ease-out",
  },
  headerMeta: { marginTop: 14, display: "flex", gap: 12, flexWrap: "wrap" },
  metaPill: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 8,
    padding: "4px 12px",
    background: VAR_PAPER,
    border: `1px solid ${VAR_PAPER_EDGE}`,
    borderRadius: 2,
    fontSize: "0.78rem",
    transition: THEME_TRANSITION,
  },
  metaLabel: {
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: VAR_INK_MUTED,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.65rem",
    transition: "color 0.4s ease-out",
  },
  metaValue: { color: VAR_ACCENT, fontStyle: "italic", transition: "color 0.4s ease-out" },

  // Tone shifts of metaPill for the turn-balance indicator.
  // Amber when balance < 50, red when balance < 10 (per BILLING_CONTRACT §6).
  // Reuses the existing palette: #d4a574 (amber/primary) and #c47a5a (red/error).
  metaPillWarn: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 8,
    padding: "4px 12px",
    background: "rgba(212,165,116,0.12)",
    border: "1px solid rgba(212,165,116,0.45)",
    borderRadius: 2,
    fontSize: "0.78rem",
  },
  metaPillDanger: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 8,
    padding: "4px 12px",
    background: "rgba(196,122,90,0.14)",
    border: "1px solid rgba(196,122,90,0.55)",
    borderRadius: 2,
    fontSize: "0.78rem",
  },
  metaValueWarn: { color: "#e8c896", fontStyle: "italic", fontWeight: 600 },
  metaValueDanger: { color: "#e8b09a", fontStyle: "italic", fontWeight: 600 },

  // Speaker-icon button in the header that toggles Phase A audio mute.
  // Matches the metaPill family visually (paper background, paper-edge
  // border, small height) but reads as a square icon button. Muted-by-
  // default, so this is most often a "click to enable" affordance.
  headerAudioBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 26,
    padding: 0,
    background: VAR_PAPER,
    border: `1px solid ${VAR_PAPER_EDGE}`,
    borderRadius: 2,
    cursor: "pointer",
    color: VAR_INK_MUTED,
    fontFamily: "inherit",
    fontSize: "0.95rem",
    lineHeight: 1,
    margin: 0,
    transition:
      "background 0.4s ease-out, border-color 0.4s ease-out, color 0.4s ease-out",
  },
  // Two-pane wrapper around <header> + <main>. Default behaviour is the
  // pre-existing single-column stack (chrome on top, content below). On
  // viewports ≥ 900px wide, App.tsx's <style> block (className
  // ember-game-twocol) flips this to row layout: chrome becomes a fixed
  // 320px left sidebar with the inline TrackingMap, content fills the
  // right pane.
  gameTwoCol: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
  },
  main: {
    position: "relative",
    zIndex: 2,
    flex: 1,
    display: "flex",
    overflow: "hidden",
    minHeight: 0,
  },
  // Left column — wraps the log + input bar so the input is pinned only under the log.
  leftColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
  },
  logPane: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "32px 48px 24px",
    fontSize: "1.08rem",
    lineHeight: 1.7,
  },
  narration: {
    marginBottom: "1.4em",
    color: VAR_INK,
    maxWidth: "62ch",
    transition: "color 0.4s ease-out",
  },
  inputLine: {
    margin: "0.6em 0 1.2em",
    display: "flex",
    gap: 10,
    color: VAR_ACCENT,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.95rem",
    transition: "color 0.4s ease-out",
  },
  inputArrow: { color: VAR_ACCENT, fontWeight: 700, opacity: 0.7, transition: "color 0.4s ease-out" },
  inputText: { fontStyle: "italic" },
  systemLine: {
    margin: "0.4em 0 1em",
    color: VAR_INK_MUTED,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.82rem",
    fontStyle: "italic",
    paddingLeft: 8,
    borderLeft: `2px solid ${VAR_PAPER_EDGE}`,
    maxWidth: "62ch",
    transition: THEME_TRANSITION,
  },
  errorLine: {
    margin: "0.4em 0 1em",
    color: "#c47a5a",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.82rem",
    paddingLeft: 8,
    borderLeft: "2px solid rgba(196,122,90,0.4)",
    maxWidth: "62ch",
    background: "rgba(196,122,90,0.05)",
    padding: "8px 12px",
    wordBreak: "break-word",
  },
  errorRetryBtn: {
    marginTop: 6,
    background: "transparent",
    border: "1px solid var(--accent-muted, rgba(212,165,116,0.45))",
    color: "var(--accent, #d4a574)",
    padding: "4px 12px",
    fontSize: "0.85rem",
    fontFamily: "inherit",
    fontStyle: "italic",
    cursor: "pointer",
    borderRadius: 2,
  },
  thinking: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: VAR_INK_MUTED,
    fontStyle: "italic",
    fontSize: "0.9rem",
    margin: "0.6em 0",
    transition: "color 0.4s ease-out",
  },
  thinkingDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: VAR_ACCENT,
    animation: "pulse 1.4s ease-in-out infinite",
    display: "inline-block",
    transition: "background 0.4s ease-out",
  },
  thinkingText: { marginLeft: 6, letterSpacing: "0.08em" },
  endCard: {
    margin: "2em 0",
    padding: "20px 24px",
    border: `1px solid ${VAR_ACCENT_MUTED}`,
    background: VAR_PAPER,
    color: VAR_INK,
    fontStyle: "italic",
    fontSize: "1.1rem",
    textAlign: "center",
    transition: THEME_TRANSITION,
  },
  endHint: {
    marginTop: 8,
    fontSize: "0.8rem",
    color: VAR_INK_MUTED,
    fontFamily: "'JetBrains Mono', monospace",
    fontStyle: "normal",
    letterSpacing: "0.1em",
    transition: "color 0.4s ease-out",
  },
  sidePane: {
    width: 260,
    flexShrink: 0,
    minHeight: 0,
    borderLeft: `1px solid ${VAR_PAPER_EDGE}`,
    padding: "32px 24px",
    overflowY: "auto",
    background: VAR_PAPER,
    fontSize: "0.82rem",
    transition: THEME_TRANSITION,
  },
  mapTitle: {
    textTransform: "uppercase",
    letterSpacing: "0.15em",
    color: VAR_INK_MUTED,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.7rem",
    marginBottom: 12,
    transition: "color 0.4s ease-out",
  },
  mapRoom: {
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: `1px dashed ${VAR_PAPER_EDGE}`,
    transition: "border-color 0.4s ease-out",
  },
  mapRoomCurrent: { color: VAR_INK },
  mapRoomName: { fontStyle: "italic", color: VAR_ACCENT, transition: "color 0.4s ease-out" },
  mapRoomExits: {
    fontSize: "0.7rem",
    color: VAR_INK_MUTED,
    fontFamily: "monospace",
    marginTop: 2,
    opacity: 0.7,
    transition: "color 0.4s ease-out",
  },
  mapPuzzle: {
    color: VAR_ACCENT,
    fontStyle: "italic",
    fontSize: "0.78rem",
    marginBottom: 6,
    paddingLeft: 4,
    wordBreak: "break-word",
    opacity: 0.85,
    transition: "color 0.4s ease-out",
  },
  inputBar: {
    position: "relative",
    zIndex: 2,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 48px 24px",
    borderTop: `1px solid ${VAR_PAPER_EDGE}`,
    background:
      "linear-gradient(to top, rgba(20,14,10,0.8), transparent)",
    transition: "border-color 0.4s ease-out",
  },
  prompt: {
    color: VAR_ACCENT,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "1.2rem",
    fontWeight: 700,
    animation: "flicker 3s ease-in-out infinite",
    transition: "color 0.4s ease-out",
  },
  input: {
    flex: 1,
    background: "transparent",
    border: "none",
    outline: "none",
    color: VAR_INK,
    fontFamily: VAR_FONT_BODY,
    fontStyle: "italic",
    fontSize: "1.15rem",
    padding: "8px 0",
    borderBottom: `1px solid ${VAR_ACCENT_MUTED}`,
    transition: THEME_TRANSITION,
  },
  submitBtn: {
    background: "transparent",
    border: `1px solid ${VAR_ACCENT_MUTED}`,
    color: VAR_ACCENT,
    width: 38,
    height: 38,
    borderRadius: 2,
    fontSize: "1.1rem",
    transition: "all 0.2s, color 0.4s ease-out, border-color 0.4s ease-out",
  },

  // ── Toast (non-blocking save warnings etc.) ─────────────────────────────
  toastWrap: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 50,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    pointerEvents: "none",
  },
  toast: {
    pointerEvents: "auto",
    padding: "10px 14px",
    background: "rgba(28,18,12,0.95)",
    border: "1px solid rgba(196,122,90,0.5)",
    color: "#e8c896",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.78rem",
    borderRadius: 2,
    boxShadow: "0 4px 18px rgba(0,0,0,0.5)",
    maxWidth: 320,
    animation: "fadein 0.3s ease-out",
  },

  // ── User chip + sign-out (header) ───────────────────────────────────────
  userChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "4px 12px",
    background: VAR_PAPER,
    border: `1px solid ${VAR_PAPER_EDGE}`,
    borderRadius: 2,
    fontSize: "0.78rem",
    transition: THEME_TRANSITION,
  },
  userName: { color: VAR_ACCENT, fontStyle: "italic", transition: "color 0.4s ease-out" },
  signOutBtn: {
    background: "transparent",
    border: "none",
    color: VAR_INK_MUTED,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.7rem",
    cursor: "pointer",
    textDecoration: "underline",
    letterSpacing: "0.08em",
    padding: 0,
    transition: "color 0.4s ease-out",
  },

  // ── Login screen ────────────────────────────────────────────────────────
  // The login screen is themed per-scenario. Colours, gradient and body
  // font are pulled from CSS custom properties (--accent, --accent-muted,
  // --ink, --ink-muted, --paper, --paper-edge, --bg-gradient, --font-body)
  // set on a wrapping element by Login.tsx. As the user previews/selects
  // a different scenario, only those vars change and the whole screen
  // recolours together.
  //
  // The wrapper element is `loginRoot` below — Login.tsx sets the
  // scenario-specific values there. `loginShell` sits inside it and
  // inherits the values via var(...) lookups. (The original loginShell
  // hardcoded these values; that path is preserved by the default-theme
  // values applied at the wrapper level.)
  loginRoot: {
    // Smooth colour swap as theme variables change. Keep transitions to
    // colour-bearing properties only — no transform here, so layout
    // doesn't shift during a theme swap.
    transition:
      "background 0.35s ease-out, color 0.35s ease-out",
  },
  loginShell: {
    position: "relative",
    minHeight: "100vh",
    background: "var(--bg-gradient, radial-gradient(ellipse at top, #1a1410 0%, #0d0907 60%, #050302 100%))",
    color: "var(--ink, #e8d9b8)",
    fontFamily: "var(--font-body, 'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    // overflow stays visible (default) so `position: sticky` on the
    // two-column auth side uses the body as its scroll context. With
    // overflow: auto here, sticky would be trapped inside this shell and
    // wouldn't track body scroll.
    transition:
      "background 0.35s ease-out, color 0.35s ease-out",
  },
  loginCard: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: 460,
    // box-sizing critical on narrow viewports — without it the 40px
    // horizontal padding adds to the 100% width and overflows iPhones
    // (375px viewport - 48px shell padding = 327px parent; card was
    // computing as 327 + 80 padding = 407px → horizontal scroll).
    boxSizing: "border-box",
    // Padding tuned to feel generous on desktop without crushing the
    // 320–414px viewport range. The clamp() keeps inner padding tight
    // on small screens and breathes on larger ones.
    padding: "clamp(28px, 7vw, 44px) clamp(20px, 6vw, 40px)",
    border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
    background: "var(--paper, rgba(20,14,10,0.55))",
    boxShadow: "0 0 60px rgba(0,0,0,0.6)",
    transition:
      "background 0.35s ease-out, border-color 0.35s ease-out",
  },
  loginTitle: {
    margin: 0,
    fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
    fontSize: "2.4rem",
    fontWeight: 400,
    color: "var(--ink, #e8d9b8)",
    letterSpacing: "0.02em",
    textAlign: "center",
    textShadow: "0 0 24px rgba(0,0,0,0.4)",
  },
  loginSub: {
    marginTop: 4,
    marginBottom: 28,
    fontStyle: "italic",
    color: "var(--ink-muted, #8a7355)",
    fontSize: "0.85rem",
    letterSpacing: "0.08em",
    textAlign: "center",
  },
  loginStepLabel: {
    textTransform: "uppercase",
    letterSpacing: "0.18em",
    color: "var(--ink-muted, #8a7355)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.7rem",
    marginBottom: 8,
    textAlign: "center",
  },
  loginHeading: {
    margin: 0,
    marginBottom: 8,
    fontSize: "1.3rem",
    color: "var(--ink, #e8d9b8)",
    fontStyle: "italic",
    textAlign: "center",
  },
  loginBody: {
    color: "var(--ink-muted, #8a7355)",
    fontSize: "0.92rem",
    textAlign: "center",
    marginBottom: 22,
    lineHeight: 1.5,
  },
  loginField: {
    width: "100%",
    boxSizing: "border-box",
    background: "transparent",
    border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
    color: "var(--ink, #e8d9b8)",
    fontFamily: "var(--font-body, 'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif)",
    fontSize: "1.05rem",
    padding: "10px 14px",
    outline: "none",
    borderRadius: 2,
  },
  loginCodeField: {
    width: "100%",
    boxSizing: "border-box",
    background: "transparent",
    border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
    color: "var(--ink, #e8d9b8)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "1.6rem",
    letterSpacing: "0.5em",
    textAlign: "center",
    padding: "14px 14px",
    outline: "none",
    borderRadius: 2,
  },
  loginPrimary: {
    width: "100%",
    boxSizing: "border-box",
    background: "transparent",
    border: "1px solid var(--accent-muted, rgba(212,165,116,0.45))",
    color: "var(--ink, #e8d9b8)",
    fontFamily: "'IM Fell English', Georgia, serif",
    fontSize: "1.05rem",
    letterSpacing: "0.06em",
    padding: "12px 16px",
    cursor: "pointer",
    borderRadius: 2,
    transition: "all 0.2s",
  },
  loginSecondary: {
    width: "100%",
    boxSizing: "border-box",
    background: "transparent",
    border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
    color: "var(--ink-muted, #8a7355)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.85rem",
    padding: "10px 14px",
    cursor: "pointer",
    borderRadius: 2,
  },
  loginLink: {
    background: "transparent",
    border: "none",
    color: "var(--ink-muted, #8a7355)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.78rem",
    cursor: "pointer",
    textDecoration: "underline",
    padding: 4,
    letterSpacing: "0.04em",
  },
  loginRow: {
    display: "flex",
    gap: 10,
    justifyContent: "center",
    flexWrap: "wrap",
    marginTop: 12,
  },
  loginDivider: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "20px 0",
    color: "var(--ink-muted, #8a7355)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.7rem",
    letterSpacing: "0.2em",
    opacity: 0.7,
  },
  loginDividerLine: {
    flex: 1,
    height: 1,
    background: "var(--paper-edge, rgba(212,165,116,0.18))",
  },
  loginErr: {
    margin: "0 0 16px",
    padding: "10px 12px",
    border: "1px solid rgba(196,122,90,0.5)",
    color: "#c47a5a",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.78rem",
    background: "rgba(196,122,90,0.06)",
    borderRadius: 2,
  },
  loginInfo: {
    margin: "0 0 16px",
    padding: "10px 12px",
    border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
    color: "var(--ink, #e8d9b8)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.78rem",
    background: "var(--paper, rgba(20,14,10,0.55))",
    borderRadius: 2,
  },
  loginSpinner: {
    display: "inline-block",
    width: 14,
    height: 14,
    border: "2px solid var(--paper-edge, rgba(212,165,116,0.18))",
    borderTopColor: "var(--accent, #d4a574)",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    verticalAlign: "middle",
    marginRight: 8,
  },
  loginFieldLabel: {
    display: "block",
    marginBottom: 6,
    color: "var(--ink-muted, #8a7355)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: "0.14em",
  },
  loginFormGroup: { marginBottom: 16 },

  // ── Login: two-column landing layout ────────────────────────────────────
  // When the auth flow is on the "email" step, the loginShell renders a
  // marketing pane on the left and the auth card on the right (≥860px).
  // Below 860px the panes stack: marketing on top, auth below.
  loginLayout: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: 1080,
    display: "flex",
    gap: 40,
    // alignItems: flex-start lets the left column grow to its content's
    // natural height while the right column sticks at a fixed viewport-
    // anchored position. (Stretch would force both to equal heights.)
    alignItems: "flex-start",
    justifyContent: "center",
  },
  loginLayoutStacked: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: 520,
    display: "flex",
    flexDirection: "column",
    gap: 28,
    alignItems: "stretch",
  },
  landingPane: {
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: 540,
    padding: "44px 40px",
    border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
    background: "var(--paper, rgba(20,14,10,0.55))",
    boxShadow: "0 0 60px rgba(0,0,0,0.45)",
    display: "flex",
    flexDirection: "column",
    gap: 18,
    color: "var(--ink-muted, #8a7355)",
    transition:
      "background 0.35s ease-out, border-color 0.35s ease-out, color 0.35s ease-out",
  },
  landingPaneStacked: {
    width: "100%",
    boxSizing: "border-box",
    padding: "32px 28px",
    border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
    background: "var(--paper, rgba(20,14,10,0.55))",
    boxShadow: "0 0 40px rgba(0,0,0,0.4)",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    color: "var(--ink-muted, #8a7355)",
    transition:
      "background 0.35s ease-out, border-color 0.35s ease-out, color 0.35s ease-out",
  },
  landingHeroTitle: {
    margin: 0,
    fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
    fontSize: "2.6rem",
    fontWeight: 400,
    color: "var(--ink, #e8d9b8)",
    letterSpacing: "0.02em",
    textShadow: "0 0 24px rgba(0,0,0,0.45)",
    lineHeight: 1.1,
    transition: "color 0.35s ease-out",
  },
  landingHeroTagline: {
    margin: 0,
    fontStyle: "italic",
    color: "var(--ink-muted, #8a7355)",
    fontSize: "1.05rem",
    lineHeight: 1.5,
  },
  // Brand-level tagline shown above the scenario-themed hero. Cross-scenario,
  // not themed — this is the positioning the whole product owns. Hero-size,
  // serif, ink colour so it reads as the headline before the visitor's eye
  // drops to the scenario chooser.
  landingBrandTagline: {
    margin: 0,
    fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
    fontSize: "1.65rem",
    fontWeight: 400,
    color: "var(--ink, #e8d9b8)",
    letterSpacing: "0.01em",
    lineHeight: 1.2,
    textShadow: "0 0 18px rgba(0,0,0,0.4)",
  },
  landingBrandTaglineSub: {
    margin: "4px 0 4px 0",
    fontStyle: "italic",
    color: "var(--ink-muted, #8a7355)",
    fontSize: "0.98rem",
    lineHeight: 1.5,
  },
  landingDivider: {
    height: 1,
    background:
      "linear-gradient(to right, transparent, var(--accent-muted, rgba(212,165,116,0.45)), transparent)",
    margin: "6px 0",
  },
  // Aspirational, cross-scenario delivery line. Sits between the scenario-
  // themed hero tagline and the divider so it lands as the second beat the
  // visitor reads (after the brand promise). Italic + accent-coloured to
  // mark it as the platform's own voice, not part of the scenario flavour.
  // Slightly larger than landingHeroTagline so it doesn't get lost.
  landingPlatformPromise: {
    margin: "2px 0",
    fontFamily: "var(--font-body, 'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif)",
    fontStyle: "italic",
    fontSize: "1.18rem",
    lineHeight: 1.45,
    color: "var(--accent, #d4a574)",
    textAlign: "center",
    letterSpacing: "0.01em",
    transition: "color 0.35s ease-out",
  },
  landingBulletList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  landingBullet: {
    display: "flex",
    gap: 14,
    alignItems: "baseline",
    lineHeight: 1.55,
  },
  landingBulletGlyph: {
    flexShrink: 0,
    color: "var(--accent, #d4a574)",
    fontSize: "1.05rem",
    lineHeight: 1,
    transform: "translateY(2px)",
    width: "1em",
    textAlign: "center",
    transition: "color 0.35s ease-out",
  },
  landingBulletText: {
    flex: 1,
    color: "var(--ink-muted, #8a7355)",
    fontStyle: "italic",
    fontSize: "0.98rem",
  },
  landingBulletTitle: {
    color: "var(--ink, #e8d9b8)",
    fontStyle: "normal",
    fontWeight: 600,
    letterSpacing: "0.01em",
    marginRight: 6,
  },
  landingLearnMoreRow: {
    marginTop: 6,
    display: "flex",
    alignItems: "center",
  },
  landingLearnMoreBtn: {
    background: "transparent",
    border: "1px solid var(--accent-muted, rgba(212,165,116,0.45))",
    color: "var(--ink, #e8d9b8)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.78rem",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    padding: "9px 16px",
    cursor: "pointer",
    borderRadius: 2,
    transition: "all 0.2s, border-color 0.35s ease-out, color 0.35s ease-out",
  },
  // Quieter, smaller value-props strip rendered between the learn-more
  // button row and the fine-print footer. Three lines, each with an accent
  // glyph; reads as a footer-pills strip rather than a headline. The
  // surrounding pane already supplies the gap, but we add a touch of top
  // margin so the strip breathes against the learn-more button.
  landingTrustStrip: {
    marginTop: 4,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  landingTrustLine: {
    display: "flex",
    gap: 10,
    alignItems: "baseline",
    color: "var(--ink-muted, #8a7355)",
    fontFamily: "var(--font-body, 'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif)",
    fontSize: "0.85rem",
    lineHeight: 1.5,
    letterSpacing: "0.01em",
  },
  landingTrustGlyph: {
    flexShrink: 0,
    color: "var(--accent, #d4a574)",
    fontSize: "0.85rem",
    lineHeight: 1,
    transform: "translateY(1px)",
    width: "1em",
    textAlign: "center",
    transition: "color 0.35s ease-out",
  },
  landingFinePrint: {
    marginTop: "auto",
    paddingTop: 18,
    borderTop: "1px dashed var(--paper-edge, rgba(212,165,116,0.18))",
    color: "var(--ink-muted, #8a7355)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.7rem",
    lineHeight: 1.6,
    letterSpacing: "0.02em",
    opacity: 0.85,
  },
  landingFinePrintEm: {
    color: "var(--ink, #e8d9b8)",
    fontStyle: "normal",
  },
  // Auth-side wrapper when in two-column landing mode. Sticky-anchored at
  // top: 24 (matching loginShell padding) and sized to the padded viewport
  // height, so the auth card stays vertically centred in the window as
  // the user scrolls the left (marketing) column. If the card itself
  // grows taller than the viewport, the column scrolls internally
  // instead of pushing the body. Sticky works because loginShell drops
  // its `overflow: auto` — the body becomes the scroll context.
  loginAuthSide: {
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: 460,
    alignSelf: "flex-start",
    position: "sticky",
    top: 24,
    height: "calc(100vh - 48px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflowY: "auto",
  },

  // ── Landing overlay (Learn more) ────────────────────────────────────────
  // Scrollable, taller variant of the modal aesthetic. Reuses modalBackdrop.
  // Themed via the same CSS variables as the rest of the login surface.
  landingOverlayCard: {
    position: "relative",
    width: "100%",
    maxWidth: 640,
    maxHeight: "calc(100vh - 64px)",
    border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
    background: "var(--paper, rgba(20,14,10,0.55))",
    boxShadow: "0 0 80px rgba(0,0,0,0.7)",
    borderRadius: 2,
    color: "var(--ink, #e8d9b8)",
    fontFamily: "var(--font-body, 'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    transition:
      "background 0.35s ease-out, border-color 0.35s ease-out, color 0.35s ease-out",
  },
  landingOverlayScroll: {
    flex: 1,
    overflowY: "auto",
    padding: "24px 40px 32px",
    minHeight: 0,
  },
  landingOverlayEyebrow: {
    textTransform: "uppercase",
    letterSpacing: "0.18em",
    color: "var(--ink-muted, #8a7355)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.7rem",
    textAlign: "center",
    marginBottom: 10,
  },
  landingOverlayTitle: {
    margin: 0,
    fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
    fontSize: "2rem",
    fontWeight: 400,
    color: "var(--ink, #e8d9b8)",
    letterSpacing: "0.02em",
    textAlign: "center",
    textShadow: "0 0 24px rgba(0,0,0,0.4)",
    lineHeight: 1.15,
  },
  landingOverlayTagline: {
    fontStyle: "italic",
    color: "var(--ink-muted, #8a7355)",
    fontSize: "1.02rem",
    textAlign: "center",
    margin: "12px 0 28px",
    lineHeight: 1.5,
  },
  // Wrapper heading for the "How Ember Hollow differs" block at the bottom
  // of the overlay — a platform-level differentiator section that sits
  // beneath the scenario-themed about-sections. Mirrors landingOverlayTitle
  // visually but a touch smaller (~1.3rem) so it doesn't compete with the
  // scenario About title at the top of the overlay.
  landingOverlayDifferHeading: {
    margin: "8px 0 4px",
    fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
    fontSize: "1.3rem",
    fontWeight: 400,
    color: "var(--ink, #e8d9b8)",
    letterSpacing: "0.02em",
    textAlign: "center",
    textShadow: "0 0 18px rgba(0,0,0,0.35)",
    lineHeight: 1.2,
  },
  landingOverlaySection: {
    marginTop: 22,
  },
  landingOverlaySectionLabel: {
    textTransform: "uppercase",
    letterSpacing: "0.16em",
    color: "var(--ink-muted, #8a7355)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.68rem",
    marginBottom: 6,
  },
  landingOverlaySectionTitle: {
    margin: "0 0 8px",
    fontFamily: "'IM Fell English', Georgia, serif",
    fontWeight: 400,
    fontSize: "1.25rem",
    color: "var(--ink, #e8d9b8)",
    letterSpacing: "0.01em",
  },
  landingOverlaySectionBody: {
    margin: 0,
    color: "var(--ink-muted, #8a7355)",
    fontSize: "1rem",
    lineHeight: 1.65,
  },
  landingOverlaySectionRule: {
    height: 1,
    background:
      "linear-gradient(to right, transparent, var(--paper-edge, rgba(212,165,116,0.18)), transparent)",
    marginTop: 22,
  },
  landingOverlayFooter: {
    flexShrink: 0,
    padding: "16px 40px 22px",
    borderTop: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
    background: "rgba(10,7,5,0.55)",
    display: "flex",
    justifyContent: "center",
  },
  landingOverlayFooterBtn: {
    background: "transparent",
    border: "1px solid var(--accent-muted, rgba(212,165,116,0.45))",
    color: "var(--ink, #e8d9b8)",
    fontFamily: "'IM Fell English', Georgia, serif",
    fontSize: "1rem",
    letterSpacing: "0.06em",
    padding: "11px 26px",
    cursor: "pointer",
    borderRadius: 2,
    transition: "all 0.2s, border-color 0.35s ease-out, color 0.35s ease-out",
  },

  // ── Paywall modal ───────────────────────────────────────────────────────
  // Centred overlay with backdrop blur, NOT a full-screen takeover.
  // Aesthetic mirrors the loginCard: dark gradient, IM Fell English title,
  // JetBrains Mono labels, warm tan palette. No new colours.
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 300,
    background: "rgba(5,3,2,0.62)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    display: "flex",
    // `safe center` centers the card when it fits, but falls back to
    // start-alignment when the card is taller than the viewport — so the
    // top doesn't get clipped and the close button stays reachable.
    // Modern Safari (16+) / Chromium / Firefox all support it.
    alignItems: "safe center",
    justifyContent: "center",
    // Backdrop itself scrolls when the card overflows, so a tall Paywall
    // on a small phone is fully readable + the close button is always
    // reachable (scroll back to top).
    overflowY: "auto",
    padding: 24,
    animation: "fadein 0.25s ease-out",
  },
  modalCard: {
    position: "relative",
    width: "100%",
    maxWidth: 480,
    // box-sizing critical on narrow viewports — without it, the 36px
    // horizontal padding adds to the 100% width and overflows phones
    // narrower than ~408px. Same fix as loginCard.
    boxSizing: "border-box",
    // Pad scales gently on narrow screens so the card breathes on
    // phones without crushing desktop's generous spacing.
    padding: "clamp(28px, 6vw, 40px) clamp(20px, 6vw, 36px) clamp(24px, 5vw, 32px)",
    border: "1px solid rgba(212,165,116,0.25)",
    background:
      "linear-gradient(to bottom, rgba(28,20,14,0.96) 0%, rgba(14,10,7,0.96) 100%)",
    boxShadow: "0 0 80px rgba(0,0,0,0.7)",
    borderRadius: 2,
    color: "#e8d9b8",
    fontFamily: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
    // Vertical breathing room at the top/bottom when content overflows
    // and the user scrolls — without this, the card sits flush against
    // the backdrop edges.
    marginBlock: "auto",
  },
  modalCloseBtn: {
    position: "absolute",
    top: 12,
    right: 14,
    zIndex: 1,
    background: "transparent",
    border: "none",
    color: "#7a6243",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "1.2rem",
    cursor: "pointer",
    padding: 6,
    lineHeight: 1,
    letterSpacing: 0,
  },
  modalEyebrow: {
    textTransform: "uppercase",
    letterSpacing: "0.18em",
    color: "#7a6243",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.7rem",
    textAlign: "center",
    marginBottom: 10,
  },
  modalTitle: {
    margin: 0,
    fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
    fontSize: "1.7rem",
    fontWeight: 400,
    color: "#e8c896",
    letterSpacing: "0.02em",
    textAlign: "center",
    textShadow: "0 0 24px rgba(212,165,116,0.25)",
    lineHeight: 1.2,
  },
  modalBody: {
    color: "#a89070",
    fontSize: "0.95rem",
    textAlign: "center",
    margin: "14px 0 24px",
    lineHeight: 1.55,
  },
  modalCtaPrimary: {
    width: "100%",
    boxSizing: "border-box",
    background: "rgba(212,165,116,0.14)",
    border: "1px solid rgba(212,165,116,0.5)",
    color: "#e8c896",
    fontFamily: "'IM Fell English', Georgia, serif",
    fontSize: "1.05rem",
    letterSpacing: "0.04em",
    padding: "12px 16px",
    cursor: "pointer",
    borderRadius: 2,
    transition: "all 0.2s",
    textAlign: "center",
  },
  modalCtaSecondary: {
    width: "100%",
    boxSizing: "border-box",
    background: "transparent",
    border: "1px solid rgba(212,165,116,0.28)",
    color: "#d4b88a",
    fontFamily: "'IM Fell English', Georgia, serif",
    fontSize: "0.98rem",
    letterSpacing: "0.04em",
    padding: "11px 16px",
    cursor: "pointer",
    borderRadius: 2,
    transition: "all 0.2s",
    textAlign: "center",
    marginTop: 10,
  },
  modalCtaSubtitle: {
    display: "block",
    marginTop: 4,
    color: "#7a6243",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.66rem",
    letterSpacing: "0.1em",
    fontStyle: "normal",
    textTransform: "none",
  },
  modalTertiaryRow: {
    display: "flex",
    justifyContent: "center",
    marginTop: 18,
  },
  modalTertiaryLink: {
    background: "transparent",
    border: "none",
    color: "#7a6243",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.74rem",
    cursor: "pointer",
    textDecoration: "underline",
    padding: 4,
    letterSpacing: "0.06em",
  },
  modalErr: {
    margin: "0 0 14px",
    padding: "10px 12px",
    border: "1px solid rgba(196,122,90,0.5)",
    color: "#c47a5a",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.78rem",
    background: "rgba(196,122,90,0.06)",
    borderRadius: 2,
  },

  // ── Header pill / dropdown machinery ────────────────────────────────────
  // Wrapper that hosts a clickable/hoverable header pill plus its absolute
  // dropdown menu. `position: relative` so the menu can anchor to it.
  metaPillWrap: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
  },
  // Button-flavoured metaPill — same visual register but interactive. Hover
  // brightens the background and border subtly.
  metaPillButton: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 8,
    padding: "4px 12px",
    background: VAR_PAPER,
    border: `1px solid ${VAR_PAPER_EDGE}`,
    borderRadius: 2,
    fontSize: "0.78rem",
    cursor: "pointer",
    color: "inherit",
    fontFamily: "inherit",
    margin: 0,
    transition:
      "background 0.4s ease-out, border-color 0.4s ease-out",
  },
  metaPillButtonHover: {
    background: VAR_PAPER,
    borderColor: VAR_ACCENT_MUTED,
    // Slight overlay on hover via box-shadow keeps the recolouring obvious
    // even as the underlying paper colour changes between scenarios.
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
  },
  metaPillButtonWarn: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 8,
    padding: "4px 12px",
    background: "rgba(212,165,116,0.12)",
    border: "1px solid rgba(212,165,116,0.45)",
    borderRadius: 2,
    fontSize: "0.78rem",
    cursor: "pointer",
    color: "inherit",
    fontFamily: "inherit",
    margin: 0,
    transition:
      "background 0.18s ease-out, border-color 0.18s ease-out",
  },
  metaPillButtonWarnHover: {
    background: "rgba(212,165,116,0.22)",
    borderColor: "rgba(212,165,116,0.65)",
  },
  metaPillButtonDanger: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 8,
    padding: "4px 12px",
    background: "rgba(196,122,90,0.14)",
    border: "1px solid rgba(196,122,90,0.55)",
    borderRadius: 2,
    fontSize: "0.78rem",
    cursor: "pointer",
    color: "inherit",
    fontFamily: "inherit",
    margin: 0,
    transition:
      "background 0.18s ease-out, border-color 0.18s ease-out",
  },
  metaPillButtonDangerHover: {
    background: "rgba(196,122,90,0.22)",
    borderColor: "rgba(196,122,90,0.75)",
  },

  // Generic dropdown menu container shared by the carrying / known
  // dropdowns. Anchored to the trigger via top:100%, left:0. Z-index
  // matches userMenu so they share a layer (above panes, below modals).
  headerMenu: {
    position: "absolute",
    // Touch the trigger directly so cursor moves between trigger and menu
    // without crossing dead space. Visual separation comes from the trigger's
    // bottom border + the menu's own top border + box-shadow.
    top: "100%",
    left: 0,
    zIndex: 30,
    minWidth: 280,
    maxWidth: 360,
    maxHeight: "calc(100vh - 140px)",
    overflowY: "auto",
    padding: "6px 0",
    background: VAR_PAPER,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: `1px solid ${VAR_ACCENT_MUTED}`,
    borderRadius: 2,
    boxShadow: "0 8px 28px rgba(0,0,0,0.6)",
    animation: "fadein 0.18s ease-out",
    color: VAR_INK,
    fontFamily: VAR_FONT_BODY,
    transition: THEME_TRANSITION,
  },
  // Right-anchored variant (for the user chip on the far right of the bar).
  headerMenuRight: {
    left: "auto",
    right: 0,
  },
  // A row in the known-rooms dropdown: italic name on top, exits below.
  headerMenuRoom: {
    padding: "8px 14px",
    borderBottom: `1px dashed ${VAR_PAPER_EDGE}`,
    transition: "border-color 0.4s ease-out",
  },
  headerMenuRoomCurrent: {
    background: VAR_PAPER,
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.03)",
  },
  headerMenuRoomName: {
    fontStyle: "italic",
    color: VAR_ACCENT,
    fontSize: "0.92rem",
    lineHeight: 1.3,
    transition: "color 0.4s ease-out",
  },
  headerMenuRoomNameCurrent: {
    color: VAR_INK,
  },
  headerMenuRoomExits: {
    fontSize: "0.68rem",
    color: VAR_INK_MUTED,
    fontFamily: "'JetBrains Mono', monospace",
    marginTop: 2,
    letterSpacing: "0.04em",
    opacity: 0.8,
    transition: "color 0.4s ease-out",
  },
  // A row in the carrying dropdown: italic name on top, dim description below.
  headerMenuItem: {
    padding: "8px 14px",
    borderBottom: `1px dashed ${VAR_PAPER_EDGE}`,
    transition: "border-color 0.4s ease-out",
  },
  headerMenuItemName: {
    fontStyle: "italic",
    color: VAR_ACCENT,
    fontSize: "0.92rem",
    lineHeight: 1.3,
    transition: "color 0.4s ease-out",
  },
  headerMenuItemDesc: {
    fontSize: "0.78rem",
    color: VAR_INK_MUTED,
    marginTop: 3,
    lineHeight: 1.5,
    transition: "color 0.4s ease-out",
  },
  headerMenuEmpty: {
    padding: "12px 14px",
    color: VAR_INK_MUTED,
    fontStyle: "italic",
    fontSize: "0.85rem",
    textAlign: "center",
    transition: "color 0.4s ease-out",
  },

  // ── User-chip dropdown menu (header) ────────────────────────────────────
  userChipBtn: {
    background: "transparent",
    border: "none",
    color: VAR_ACCENT,
    fontFamily: VAR_FONT_BODY,
    fontStyle: "italic",
    fontSize: "0.85rem",
    cursor: "pointer",
    padding: 0,
    margin: 0,
    letterSpacing: "0.02em",
    transition: "color 0.4s ease-out",
  },
  userMenuWrap: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
  },
  userMenu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    zIndex: 30,
    minWidth: 200,
    padding: "6px 0",
    background: VAR_PAPER,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: `1px solid ${VAR_ACCENT_MUTED}`,
    borderRadius: 2,
    boxShadow: "0 8px 28px rgba(0,0,0,0.6)",
    animation: "fadein 0.18s ease-out",
    transition: THEME_TRANSITION,
  },
  userMenuItem: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    background: "transparent",
    border: "none",
    color: VAR_ACCENT,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.78rem",
    textAlign: "left",
    padding: "9px 14px",
    cursor: "pointer",
    letterSpacing: "0.04em",
    transition: "color 0.4s ease-out",
  },
  userMenuDivider: {
    height: 1,
    background: VAR_PAPER_EDGE,
    margin: "4px 0",
    transition: "background 0.4s ease-out",
  },
  // Indented submenu radio rows (e.g. Guidance: Read / Classic / Hard).
  // Sits beneath a parent userMenuItem and reads as part of the same menu.
  userMenuSubrow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    boxSizing: "border-box",
    background: "transparent",
    border: "none",
    color: VAR_INK_MUTED,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.74rem",
    textAlign: "left",
    padding: "7px 14px 7px 26px",
    cursor: "pointer",
    letterSpacing: "0.04em",
    transition: "color 0.4s ease-out",
  },
  userMenuSubrowSelected: {
    color: VAR_INK,
    background: VAR_PAPER,
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
  },
  userMenuRadio: {
    color: VAR_ACCENT,
    fontSize: "0.85rem",
    width: 12,
    display: "inline-block",
    textAlign: "center",
    transition: "color 0.4s ease-out",
  },

  // ── Billing return card (success / cancel from Stripe) ──────────────────
  // Reuses the centred-card shell aesthetic of the login screen. Themed
  // via the same vars as the in-game shell so it picks up the active
  // scenario palette when shown mid-game.
  billingReturnShell: {
    position: "relative",
    minHeight: "100vh",
    background: VAR_BG,
    color: VAR_INK,
    fontFamily: VAR_FONT_BODY,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    overflow: "auto",
    transition: THEME_TRANSITION,
  },
  billingReturnCard: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: 460,
    padding: "44px 40px",
    border: `1px solid ${VAR_ACCENT_MUTED}`,
    background: VAR_PAPER,
    boxShadow: "0 0 60px rgba(0,0,0,0.6)",
    borderRadius: 2,
    textAlign: "center",
    transition: THEME_TRANSITION,
  },
};
